/* =====================================================================
   ENTITIES — игрок, враги, снаряды, подбираемые, частицы, числа урона.
   ===================================================================== */

import { CONFIG } from './config';
import { RNG, TAU, clamp } from './utils';
import { Meta } from './meta';

export class Player {
  // Динамический набор полей (свет/статы/прокачка) — типизируется прагматично.
  [key: string]: any;

  constructor() {
    this.reset();
  }
  reset(charKey?: any) {
    const p = CONFIG.player;
    this.x = 0; this.y = 0;
    this.vx = 0; this.vy = 0;
    this.facing = -Math.PI / 2;     // куда смотрит (для хлыста)
    this.lastDir = { x: 1, y: 0 };
    this.radius = p.radius;
    this.level = 1;
    this.xp = 0;
    this.xpNext = CONFIG.xp.base;
    this.invuln = 0;
    this.flash = 0;
    this.alive = true;
    this.kills = 0;
    this.gold = 0;
    // прокачиваемые наборы
    this.weapons = [];               // [{key, level, timer, ...}]
    this.passives = {};              // {key: level}
    // базовые статы + модификаторы персонажа
    this.base = Object.assign({}, p);
    const ch = CONFIG.characters[charKey] || CONFIG.characters.spark;
    this.charKey = (CONFIG.characters[charKey] ? charKey : 'spark');
    const m = ch.mods || {};
    if (m.maxHp) this.base.maxHp = m.maxHp;
    if (m.moveSpeedMul) this.base.moveSpeed = p.moveSpeed * m.moveSpeedMul;
    if (m.damageMult) this.base.damageMult = m.damageMult;
    this._charLightBonus = m.lightBonus || 0;
    // особые режимы света героев: 'decay' (Угасающий), 'mirror' (Зеркало)
    this._lightMode = m.lightMode || null;
    this._lightDecay = m.lightDecay || 0;     // px/сек угасания (decay)
    this._killLight = m.killLight || 0;       // +свет за убийство (decay)
    this._mirrorPer = m.mirrorPer || 0;       // свет за осколок (mirror)
    this._mirrorStacks = 0;
    this._lightFloor = 0; this._lightFloorT = 0;   // удержание света от ульты-вспышки
    // билдовые пассивки (производные, считаются в recalc) + вклад реликвий
    this.critChance = 0; this.cooldownMult = 1; this.leech = 0;
    this.bloomDmg = 0; this.bloomR = 0;
    this.frostMul = 1; this.frostR = 0;
    this.pulseDmg = 0; this.pulseR = 0;
    this._relicLeech = 0; this._relicCdMul = 1; this._auraThorns = false;
    this._snareDR = 1; this._snareCdMul = 1; this._snareDmgMul = 1;   // эффекты опоры Тенёты — чисто на новый забег (если умер в удержании, endSnare не вызвался)
    this._loEdge = 0; this._hiEdge = 0;   // билд-ось Границы (свет): бонус урона при низком/широком жаре
    this._synLo = 0; this._synHi = 0; this._synergies = [];   // синергии оружия (вклад в полюса + список активных)
    this._emberDensity = 0;               // плотность своей территории под игроком (синергия D2)
    // перманентные апгрейды из магазина (мета)
    this._revives = 0;
    if (typeof Meta !== 'undefined' && Meta.data && Meta.data.upgrades) {
      const u = Meta.data.upgrades;
      if (u.maxhp) this.base.maxHp += u.maxhp * 12;
      if (u.power) this.base.damageMult *= (1 + u.power * 0.05);
      if (u.speed) this.base.moveSpeed *= (1 + u.speed * 0.04);
      if (u.light) this._charLightBonus += u.light * 20;
      this._revives = u.revive || 0;
    }
    // производные
    this.maxHp = this.base.maxHp;
    this.hp = this.maxHp;
    this.moveSpeed = this.base.moveSpeed;
    this.pickupRadius = this.base.pickupRadius;
    this.regen = this.base.regen;
    this.damageMult = this.base.damageMult;
    this._bonusMaxHp = 0;
    if (this._lightMode === 'decay') this.light = CONFIG.light.max;        // стартует ярко, тает
    else if (this._lightMode === 'mirror') this.light = CONFIG.light.min;  // тьма, пока не нафармил
    else this.light = clamp(CONFIG.light.base + this._charLightBonus, CONFIG.light.min, CONFIG.light.max);
  }

  addWeapon(key: string) {
    const w = this.weapons.find((w: any) => w.key === key);
    if (w) { if (w.level < 5) w.level++; this.recalc(); return; }
    this.weapons.push({ key, level: 1, timer: 0, orbAngle: 0 });
    this.recalc();   // набор оружия изменился → пересчитать синергии
  }
  addPassive(key: string) {
    this.passives[key] = Math.min(5, (this.passives[key] || 0) + 1);
    this.recalc();
  }
  hasWeapon(key: string) { const w = this.weapons.find((w: any) => w.key === key); return w ? w.level : 0; }

  recalc() {
    const b = this.base;
    let moveSpeed = b.moveSpeed, pickupRadius = b.pickupRadius, regen = b.regen;
    let damageMult = b.damageMult, bonusMaxHp = 0;
    // сброс билдовых производных перед пересчётом (пассивка могла отсутствовать)
    let crit = 0, cdMul = 1, leech = 0, bloomDmg = 0, bloomR = 0;
    let frostMul = 1, frostR = 0, pulseDmg = 0, pulseR = 0;
    for (const key in this.passives) {
      const def = CONFIG.passives[key];
      const lv = this.passives[key];
      const v = def.val[lv - 1];
      if (def.mode === 'mult') {
        if (def.stat === 'moveSpeed') moveSpeed = b.moveSpeed * v;
        else if (def.stat === 'pickupRadius') pickupRadius = b.pickupRadius * v;
        else if (def.stat === 'damageMult') damageMult = b.damageMult * v;
      } else if (def.mode === 'add') {
        if (def.stat === 'regen') regen = b.regen + v;
      } else if (def.mode === 'addMaxHp') {
        bonusMaxHp = v;
      } else if (def.mode === 'special') {
        if (def.special === 'crit')  crit = v;
        else if (def.special === 'haste') cdMul = v;
        else if (def.special === 'leech') leech = v;
        else if (def.special === 'bloom') { bloomDmg = v; bloomR = def.radius[lv - 1]; }
        else if (def.special === 'frost') { frostMul = v; frostR = def.radius[lv - 1]; }
        else if (def.special === 'pulse') { pulseDmg = v; pulseR = def.radius[lv - 1]; }
      }
    }
    // СИНЕРГИИ ОРУЖИЯ: обе оружия пары → вклад в полюс (_synLo/_synHi) или ровный множитель урона
    let synLo = 0, synHi = 0, synMult = 1; const synActive: any[] = [];
    for (const s of CONFIG.synergies) {
      if (this.hasWeapon(s.a) && this.hasWeapon(s.b)) {
        if (s.edge === 'lo') synLo += s.amount;
        else if (s.edge === 'hi') synHi += s.amount;
        else synMult *= (1 + s.amount);
        synActive.push(s.key);
      }
    }
    this._synLo = synLo; this._synHi = synHi; this._synergies = synActive;
    this.moveSpeed = moveSpeed;
    this.pickupRadius = pickupRadius;
    this.regen = regen;
    this.damageMult = damageMult * synMult;
    // билдовые производные + вклад реликвий
    this.critChance = crit;
    this.cooldownMult = cdMul * (this._relicCdMul || 1);
    this.leech = leech + (this._relicLeech || 0);
    this.bloomDmg = bloomDmg; this.bloomR = bloomR;
    this.frostMul = frostMul; this.frostR = frostR;
    this.pulseDmg = pulseDmg; this.pulseR = pulseR;
    // апгрейды макс. HP поднимают ТОЛЬКО потолок, не долечивают текущее HP
    // (предложение Дмитрия из плейтеста: иначе «+HP» = бесплатное мгновенное лечение).
    // hp не должно превышать новый потолок (на случай если максимум снизился).
    this.maxHp = b.maxHp + bonusMaxHp;
    if (this.hp > this.maxHp) this.hp = this.maxHp;
  }

  gainXp(v: number) {
    this.xp += v;
    let leveled = 0;
    while (this.xp >= this.xpNext) {
      this.xp -= this.xpNext;
      this.level++;
      leveled++;
      this.xpNext = Math.round(CONFIG.xp.base * Math.pow(CONFIG.xp.growth, this.level - 1));
    }
    return leveled;
  }
}

// --- Фабрики для пулов (плоские объекты для скорости) ---

export function makeEnemy() {
  return { x:0,y:0, kx:0,ky:0, hp:0, maxHp:0, radius:0, speed:0, damage:0,
           color:'#fff', shape:'tri', xp:1, bigGem:false, score:1, isBoss:false,
           flash:0, dead:false, typeKey:'chaser', wob: RNG.next()*TAU, dmgCd:0, hitCd:0, dmgNumCd:0,
           ranged:false, shotTimer:0, shotDmg:0, shotSpeed:0, shotCd:0, shotRange:0, shotRadius:6,
           // свет-механики: высасывание света, разделение, якорь тьмы, замедление фонарём
           drainLight:0, drainRange:0, split:null, splitCount:0, isChild:false,
           anchorOnDeath:false, anchorRadius:0, anchorLife:0, slowT:0, slowMul:1, bossKind:null, bossPhase:0,
           suppressLight:false, suppressRange:0,
           ring:false, ringCd:0, ringTimer:0, ringShots:0, ringSpeed:0, ringDmg:0, ringRadius:6,
           // HG-10 контр-враги: heatImmune (иммун к жар-полю), lunge (рывок Срыва), kite (пятится Жало)
           heatImmune:false, lunge:false, lungeRange:0, lungeSpeed:0, lungeCd:0, lungeTimer:0, lungeT:0,
           kite:false, kiteRange:0,
           // Исполин: слэм по площади (телеграф slamTele >= 0 → удар)
           slam:false, slamCd:0, slamR:0, slamDmg:0, slamTimer:0, slamTele:-1,
           phase2:null };   // вторая фаза босса (config phase2) — null у обычных врагов
}
export function makeProj() {
  return { x:0,y:0, vx:0,vy:0, dmg:0, radius:4, life:0, pierce:0, color:'#fff',
           kind:'bolt', knockback:0, dead:false, hitIds:null, angle:0, ownerTick:0,
           orbIndex:0, src:null, trigger:0, hostile:false, target:null,
           slowMul:1, tick:0, tickTimer:0, maxLife:0, bounces:0, bounceGain:0,
           split:false, didSplit:false };   // глагол «Раскол» (ставит _bolt); дефолт тут — явный инвариант пула
}
export function makePickup() {
  // life>0 — осколок опыта остывает и гаснет, если его не подобрать (свет в холоде не вечен); 0 = не гаснет (золото)
  return { x:0,y:0, vx:0,vy:0, type:'xp', value:1, color:'#fff', dead:false, magnet:false, born:0, life:0 };
}
export function makeParticle() {
  // grav — вертикальное ускорение (отрицат. = угли всплывают); damp — затухание скорости (меньше = дольше дрейфует пепел)
  return { x:0,y:0, vx:0,vy:0, life:0, maxLife:0, color:'#fff', size:3, dead:false, fade:1, grav:0, damp:3 };
}
export function makeDmgNum() {
  return { x:0,y:0, vy:-30, value:0, life:0, color:'#fff', crit:false, dead:false };
}
